import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition, Tab } from '@headlessui/react';
import { XMarkIcon, EnvelopeIcon, TrophyIcon, StarIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { sendPaymentConfirmationEmail, sendStatusChangeEmail, sendWelcomeEmail } from '../lib/email';
import { supabase, getPlayerAwards } from '../lib/supabase';
import { getSignedUrl } from '../lib/storage';

interface Player {
  id: number;
  name: string;
  email: string;
  phone: string;
  club: string;
  handicap: number;
  category: string;
  status: string;
  registrationDate: string;
  paymentStatus: string;
  country: string;
  registrationStatus: {
    personalInfo: boolean;
    payment: boolean;
    verification: boolean;
  };
  payment_proof_url?: string;
  shirt_size?: string;
  registration_number?: string;
  gender?: string;
}

interface PlayerAward {
  id: string;
  award_id: string;
  notes: string;
  award_categories: {
    name: string;
    description: string;
  };
}

interface PaymentRecord {
  id: number;
  amount: string;
  date: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  proofUrl: string;
  reference: string;
}

interface PlayerDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  player: Player | null;
  tournamentId?: string;
  onApprovePayment?: (paymentId: number) => void;
  onRejectPayment?: (paymentId: number) => void;
  onEditPlayer?: (player: Player) => void;
  onDeletePlayer?: (playerId: number) => void;
}

export default function PlayerDetailsModal({
  isOpen,
  onClose,
  player,
  tournamentId,
  onApprovePayment,
  onRejectPayment,
  onEditPlayer,
  onDeletePlayer
}: PlayerDetailsModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editedPlayer, setEditedPlayer] = useState<Player | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentRecords, setPaymentRecords] = useState<PaymentRecord[]>([]);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [playerAwards, setPlayerAwards] = useState<PlayerAward[]>([]);
  const [loadingAwards, setLoadingAwards] = useState(false);
  const [isLoadingProof, setIsLoadingProof] = useState(false);
  const [registrationFee, setRegistrationFee] = useState<number>(50000);
  const [tournamentCategories, setTournamentCategories] = useState<{ id: string; name: string }[]>([]);

  const handleViewProof = async () => {
    if (!player?.payment_proof_url) return;
    setIsLoadingProof(true);
    try {
      const url = new URL(player.payment_proof_url);
      const parts = url.pathname.split('/storage/v1/object/public/');
      if (parts.length < 2) {
        window.open(player.payment_proof_url, '_blank');
        return;
      }
      const fullPath = parts[1];
      const slashIndex = fullPath.indexOf('/');
      const bucket = fullPath.substring(0, slashIndex);
      const filePath = fullPath.substring(slashIndex + 1);
      const signedUrl = await getSignedUrl(filePath, bucket);
      window.open(signedUrl || player.payment_proof_url, '_blank');
    } catch {
      window.open(player.payment_proof_url, '_blank');
    } finally {
      setIsLoadingProof(false);
    }
  };

  useEffect(() => {
    if (!tournamentId) return;
    supabase
      .from('tournament_categories')
      .select('id, name')
      .eq('tournament_id', tournamentId)
      .order('sort_order')
      .order('created_at')
      .then(({ data }) => {
        if (data) setTournamentCategories(data);
      });
  }, [tournamentId]);

  useEffect(() => {
    if (player) {
      setEditedPlayer({ ...player });
      fetchPlayerAwards();
      fetchTournamentFeeAndSetPayment();
    }
  }, [player]);

  const fetchTournamentFeeAndSetPayment = async () => {
    let fee = 50000;
    try {
      const originalPlayer = await findOriginalPlayer();
      if (originalPlayer?.tournament_id) {
        const { data: tournament } = await supabase
          .from('tournaments')
          .select('registration_fee')
          .eq('id', originalPlayer.tournament_id)
          .maybeSingle();
        if (tournament?.registration_fee) {
          fee = tournament.registration_fee;
        }
      } else {
        const { data: activeTournament } = await supabase
          .from('tournaments')
          .select('registration_fee')
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (activeTournament?.registration_fee) {
          fee = activeTournament.registration_fee;
        }
      }
    } catch {
    }
    setRegistrationFee(fee);
    if (player?.registrationStatus) {
      const paymentStatus = player.registrationStatus.payment ? 'Approved' : 'Pending';
      setPaymentRecords([{
        id: 1,
        amount: `\u20A6${fee.toLocaleString()}`,
        date: player.registrationDate || new Date().toISOString().split('T')[0],
        status: paymentStatus,
        proofUrl: player.payment_proof_url || '',
        reference: player.registration_number || `REF-${Math.random().toString(36).substring(2, 10).toUpperCase()}`
      }]);
    } else {
      setPaymentRecords([]);
    }
  };

  const fetchPlayerAwards = async () => {
    if (!player) return;

    try {
      setLoadingAwards(true);

      const originalPlayer = await findOriginalPlayer();

      if (!originalPlayer) {
        console.error('Could not find original player');
        return;
      }

      const { data, error } = await getPlayerAwards(originalPlayer.id, originalPlayer.tournament_id);

      if (error) throw error;

      if (data) {
        setPlayerAwards(data);
      }
    } catch (err) {
      console.error('Error fetching player awards:', err);
    } finally {
      setLoadingAwards(false);
    }
  };

  const findOriginalPlayer = async () => {
    try {
      const { data: players, error } = await supabase
        .from('players')
        .select('*');

      if (error) throw error;

      return players?.find(p => {
        const numericId = p.id ? parseInt(p.id.replace(/-/g, '').substring(0, 8), 16) : 0;
        return numericId === player?.id;
      });
    } catch (err) {
      console.error('Error finding original player:', err);
      return null;
    }
  };

  if (!player || !editedPlayer) {
    return null;
  }

  const handleSave = async () => {
    if (!editedPlayer) return;

    try {
      const statusChanged = player.status !== editedPlayer.status;

      if (onEditPlayer) {
        await onEditPlayer(editedPlayer);
      }

      if (statusChanged) {
        const nameParts = editedPlayer.name.split(' ');
        await sendStatusChangeEmail(
          editedPlayer.email,
          nameParts[0],
          editedPlayer.status,
          nameParts.slice(1).join(' ')
        );
        toast.success(`Player updated and status change email sent`);
      } else {
        toast.success('Player updated successfully');
      }

      setIsEditing(false);
    } catch (err) {
      console.error('Error updating player:', err);
      toast.error('Failed to update player');
    }
  };

  const handleCancel = () => {
    setEditedPlayer({ ...player });
    setIsEditing(false);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (onDeletePlayer) {
      onDeletePlayer(player.id);
      setShowDeleteConfirm(false);
      onClose();
    }
  };

  const renderField = (label: string, field: keyof Player, type: string = 'text') => {
    if (field === 'registrationStatus') {
      return null;
    }

    const value = editedPlayer[field];
    const inputValue = typeof value === 'number' ? value.toString() : (value as string);

    return (
      <div>
        <label className="block text-sm text-gray-500">{label}</label>
        {isEditing ? (
          <input
            type={type}
            value={inputValue}
            onChange={(e) => {
              const newValue = type === 'number' ? Number(e.target.value) : e.target.value;
              setEditedPlayer({ ...editedPlayer, [field]: newValue });
            }}
            className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
          />
        ) : (
          <p className="text-golf-gray font-medium">{inputValue}</p>
        )}
      </div>
    );
  };

  const handleApprovePayment = async (paymentId: number) => {
    if (!onApprovePayment) return;

    try {
      setIsProcessingPayment(true);

      await onApprovePayment(paymentId);

      const updatedRecords = paymentRecords.map(record =>
        record.id === paymentId ? { ...record, status: 'Approved' as const } : record
      );
      setPaymentRecords(updatedRecords);

      try {
        const paymentNameParts = player.name.split(' ');
        const approvedRecord = updatedRecords.find(r => r.id === paymentId);
        await sendPaymentConfirmationEmail(
          player.email,
          paymentNameParts[0],
          `\u20A6${registrationFee.toLocaleString()}`,
          approvedRecord?.reference || "N/A",
          paymentNameParts.slice(1).join(' ')
        );
      } catch (emailError) {
        console.error('Error sending confirmation email:', emailError);
      }

      toast.success('Payment approved and confirmation email sent');
    } catch (err) {
      console.error('Error approving payment:', err);
      toast.error('Failed to approve payment');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleRejectPayment = async (paymentId: number) => {
    if (!onRejectPayment) return;

    try {
      setIsProcessingPayment(true);

      await onRejectPayment(paymentId);

      const updatedRecords = paymentRecords.map(record => {
        if (record.id === paymentId) {
          return { ...record, status: 'Rejected' as const };
        }
        return record;
      });
      setPaymentRecords(updatedRecords);

      toast.success('Payment rejected');
    } catch (err) {
      console.error('Error rejecting payment:', err);
      toast.error('Failed to reject payment');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleSendWelcomeEmail = async () => {
    if (!player || !player.email) {
      toast.error('Player email is missing');
      return;
    }

    try {
      setIsSendingEmail(true);

      const regNumber = player.registration_number ||
        `IBO-${new Date().getFullYear()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;

      const originalPlayer = await findOriginalPlayer();
      const tid = originalPlayer?.tournament_id || tournamentId;

      let tournamentData: { tournamentName?: string; tournamentDate?: string; venue?: string } = {};
      if (tid) {
        const { data: t } = await supabase
          .from('tournaments')
          .select('name, start_date, end_date, location')
          .eq('id', tid)
          .maybeSingle();
        if (t) {
          tournamentData.tournamentName = t.name || undefined;
          tournamentData.venue = t.location || undefined;
          if (t.start_date && t.end_date) {
            const start = new Date(t.start_date);
            const end = new Date(t.end_date);
            const opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' };
            tournamentData.tournamentDate = `${start.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
          } else if (t.start_date) {
            tournamentData.tournamentDate = new Date(t.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
          }
        }
      }

      if (!tournamentData.tournamentName) {
        const { data: active } = await supabase
          .from('tournaments')
          .select('name, start_date, end_date, location')
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (active) {
          tournamentData.tournamentName = active.name || undefined;
          tournamentData.venue = active.location || undefined;
          if (active.start_date && active.end_date) {
            const start = new Date(active.start_date);
            const end = new Date(active.end_date);
            const opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' };
            tournamentData.tournamentDate = `${start.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
          } else if (active.start_date) {
            tournamentData.tournamentDate = new Date(active.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
          }
        }
      }

      const regNameParts = player.name.split(' ');
      const result = await sendWelcomeEmail(
        player.email,
        regNameParts[0],
        regNumber,
        player.shirt_size,
        regNameParts.slice(1).join(' '),
        tournamentData
      );

      if (!result.success) {
        toast.error(result.message || 'Failed to send welcome email');
        return;
      }

      const updatedPlayer = {
        ...player,
        status: 'Active',
        registration_number: regNumber,
        registrationStatus: {
          ...player.registrationStatus,
          verification: true
        }
      };

      if (onEditPlayer) {
        await onEditPlayer(updatedPlayer);
      }

      setEditedPlayer(prev => prev ? {
        ...prev,
        status: 'Active',
        registration_number: regNumber,
        registrationStatus: {
          ...prev.registrationStatus,
          verification: true
        }
      } : prev);

      toast.success('Welcome email sent and player marked as verified');
    } catch (err) {
      console.error('Error sending welcome email:', err);
      toast.error('Failed to send welcome email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg md:rounded-2xl bg-white text-left shadow-xl transition-all w-full sm:my-8 sm:max-w-4xl mx-4 sm:mx-auto">
                {showDeleteConfirm && (
                  <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                      <h3 className="text-xl font-bold text-golf-gray mb-4">Confirm Deletion</h3>
                      <p className="text-gray-600 mb-6">
                        Are you sure you want to delete {player.name}'s account? This action cannot be undone.
                      </p>
                      <div className="flex flex-col sm:flex-row justify-end gap-3">
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          className="w-full sm:w-auto px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={confirmDelete}
                          className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-golf-green px-4 sm:px-6 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <Dialog.Title className="text-xl font-display font-bold text-white">
                      Player Details
                    </Dialog.Title>
                    <div className="flex items-center justify-end space-x-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={handleCancel}
                            className="px-3 py-2 text-white hover:bg-white/10 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSave}
                            className="px-3 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 transition-colors"
                          >
                            Save
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setIsEditing(true)}
                            className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={handleDelete}
                            className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </>
                      )}
                      <button
                        onClick={onClose}
                        className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <Tab.Group>
                  <Tab.List className="flex space-x-1 border-b border-gray-200 px-4 sm:px-6 overflow-x-auto">
                    {['Overview', 'Payments'].map((category) => (
                      <Tab
                        key={category}
                        className={({ selected }) =>
                          `py-3 sm:py-4 px-4 sm:px-6 text-sm font-medium whitespace-nowrap focus:outline-none ${
                            selected
                              ? 'text-golf-orange border-b-2 border-golf-orange'
                              : 'text-gray-500 hover:text-golf-gray'
                          }`
                        }
                      >
                        {category}
                      </Tab>
                    ))}
                  </Tab.List>

                  <Tab.Panels className="p-4 sm:p-6">
                    <Tab.Panel>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                        <div>
                          <h3 className="text-lg font-semibold text-golf-gray mb-4">Personal Information</h3>
                          <div className="space-y-4">
                            {renderField('Full Name', 'name')}
                            {renderField('Email', 'email', 'email')}
                            {renderField('Phone', 'phone', 'tel')}
                            <div>
                              <label className="block text-sm text-gray-500">Gender</label>
                              {isEditing ? (
                                <select
                                  value={editedPlayer.gender || ''}
                                  onChange={(e) => setEditedPlayer({ ...editedPlayer, gender: e.target.value })}
                                  className="mt-1 block w-full rounded-lg border-gray-300 bg-white text-sm text-golf-gray shadow-sm focus:border-golf-orange focus:ring-golf-orange [color-scheme:light]"
                                >
                                  <option value="">Select gender</option>
                                  <option value="male">Male</option>
                                  <option value="female">Female</option>
                                  <option value="other">Other</option>
                                  <option value="prefer not to say">Prefer not to say</option>
                                </select>
                              ) : (
                                <p className="text-golf-gray font-medium">{editedPlayer.gender ? editedPlayer.gender.charAt(0).toUpperCase() + editedPlayer.gender.slice(1) : 'Not specified'}</p>
                              )}
                            </div>
                            {renderField('Country', 'country')}
                            {renderField('Registration Number', 'registration_number')}
                          </div>
                        </div>

                        <div>
                          <h3 className="text-lg font-semibold text-golf-gray mb-4">Golf Information</h3>
                          <div className="space-y-4">
                            {renderField('Resident Club', 'club')}
                            {renderField('Handicap', 'handicap', 'number')}
                            <div>
                              <label className="block text-sm text-gray-500">Category</label>
                              {isEditing ? (
                                <select
                                  value={editedPlayer.category}
                                  onChange={(e) => setEditedPlayer({ ...editedPlayer, category: e.target.value })}
                                  className="mt-1 block w-full rounded-lg border-gray-300 bg-white text-sm text-golf-gray shadow-sm focus:border-golf-orange focus:ring-golf-orange [color-scheme:light]"
                                >
                                  <option value="">Select a category</option>
                                  {tournamentCategories.length > 0
                                    ? tournamentCategories.map(c => (
                                        <option key={c.id} value={c.name}>{c.name}</option>
                                      ))
                                    : (
                                      <>
                                        <option value="Professional">Professional</option>
                                        <option value="Amateur">Amateur</option>
                                        <option value="Senior">Senior</option>
                                        <option value="Junior">Junior</option>
                                      </>
                                    )
                                  }
                                </select>
                              ) : (
                                <p className="text-golf-gray font-medium">{editedPlayer.category}</p>
                              )}
                            </div>
                            {renderField('Registration Date', 'registrationDate', 'date')}
                            <div>
                              <label className="block text-sm text-gray-500">Shirt Size</label>
                              {isEditing ? (
                                <select
                                  value={editedPlayer.shirt_size || ''}
                                  onChange={(e) => setEditedPlayer({ ...editedPlayer, shirt_size: e.target.value })}
                                  className="mt-1 block w-full rounded-lg border-gray-300 bg-white text-sm text-golf-gray shadow-sm focus:border-golf-orange focus:ring-golf-orange [color-scheme:light]"
                                >
                                  <option value="">Select a size</option>
                                  <option value="XS">XS - Extra Small</option>
                                  <option value="S">S - Small</option>
                                  <option value="M">M - Medium</option>
                                  <option value="L">L - Large</option>
                                  <option value="XL">XL - Extra Large</option>
                                  <option value="XXL">XXL - Extra Extra Large</option>
                                  <option value="XXXL">XXXL - Triple Extra Large</option>
                                </select>
                              ) : (
                                <p className="text-golf-gray font-medium">{editedPlayer.shirt_size || 'Not specified'}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-8">
                        <h3 className="text-lg font-semibold text-golf-gray mb-4">Registration Status</h3>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Progress</span>
                              <span className="font-medium text-golf-gray">
                                {Object.values(editedPlayer.registrationStatus).filter(Boolean).length}/3
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-golf-orange rounded-full h-2 transition-all duration-300"
                                style={{
                                  width: `${(Object.values(editedPlayer.registrationStatus).filter(Boolean).length / 3) * 100}%`
                                }}
                              />
                            </div>
                            <div className="space-y-2">
                              <div className={`flex items-center justify-between text-sm ${editedPlayer.registrationStatus.personalInfo ? 'text-green-500' : 'text-gray-400'}`}>
                                <span className="flex items-center">
                                  <span className="w-1.5 h-1.5 rounded-full mr-1 bg-current" />
                                  Personal Info
                                </span>
                                {isEditing && (
                                  <div className="mt-2">
                                    <label className="inline-flex items-center">
                                      <input
                                        type="checkbox"
                                        checked={editedPlayer.registrationStatus.personalInfo}
                                        onChange={(e) => setEditedPlayer({
                                          ...editedPlayer,
                                          registrationStatus: {
                                            ...editedPlayer.registrationStatus,
                                            personalInfo: e.target.checked
                                          }
                                        })}
                                        className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange"
                                      />
                                      <span className="ml-2 text-sm text-gray-700">Mark as completed</span>
                                    </label>
                                  </div>
                                )}
                              </div>
                              <div className={`flex items-center justify-between text-sm ${editedPlayer.registrationStatus.payment ? 'text-green-500' : 'text-gray-400'}`}>
                                <span className="flex items-center">
                                  <span className="w-1.5 h-1.5 rounded-full mr-1 bg-current" />
                                  Payment
                                </span>
                                {isEditing && (
                                  <div className="mt-2">
                                    <label className="inline-flex items-center">
                                      <input
                                        type="checkbox"
                                        checked={editedPlayer.registrationStatus.payment}
                                        onChange={(e) => setEditedPlayer({
                                          ...editedPlayer,
                                          registrationStatus: {
                                            ...editedPlayer.registrationStatus,
                                            payment: e.target.checked
                                          }
                                        })}
                                        className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange"
                                      />
                                      <span className="ml-2 text-sm text-gray-700">Mark as completed</span>
                                    </label>
                                  </div>
                                )}
                              </div>
                              <div className={`flex items-center justify-between text-sm ${editedPlayer.registrationStatus.verification ? 'text-green-500' : 'text-gray-400'}`}>
                                <span className="flex items-center">
                                  <span className="w-1.5 h-1.5 rounded-full mr-1 bg-current" />
                                  Verified
                                </span>
                                {isEditing && (
                                  <div className="mt-2">
                                    <label className="inline-flex items-center">
                                      <input
                                        type="checkbox"
                                        checked={editedPlayer.registrationStatus.verification}
                                        onChange={(e) => setEditedPlayer({
                                          ...editedPlayer,
                                          status: e.target.checked ? 'Active' : 'Pending',
                                          registrationStatus: {
                                            ...editedPlayer.registrationStatus,
                                            verification: e.target.checked
                                          }
                                        })}
                                        className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange"
                                      />
                                      <span className="ml-2 text-sm text-gray-700">Mark as completed</span>
                                    </label>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <button
                                onClick={handleSendWelcomeEmail}
                                disabled={isSendingEmail}
                                className="flex items-center justify-center w-full px-4 py-2 bg-golf-green text-white rounded-lg hover:bg-golf-green/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <EnvelopeIcon className="w-5 h-5 mr-2" />
                                {isSendingEmail ? 'Sending Email...' : 'Send Welcome Email'}
                              </button>
                              <p className="mt-2 text-xs text-gray-500 text-center">
                                This will send a welcome email to {player.email} with arrival info, schedule, and next steps.
                              </p>
                            </div>
                            {isEditing && (
                              <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Player Status</label>
                                <select
                                  value={editedPlayer.status}
                                  onChange={(e) => setEditedPlayer({ ...editedPlayer, status: e.target.value })}
                                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                                >
                                  <option value="Pending">Pending</option>
                                  <option value="Active">Active</option>
                                  <option value="Withdrawn">Withdrawn</option>
                                </select>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Tab.Panel>

                    <Tab.Panel>
                      <div className="space-y-6">
                        {paymentRecords.length > 0 ? (
                          paymentRecords.map((payment) => (
                            <div
                              key={payment.id}
                              className="bg-white rounded-lg border border-gray-200 p-4"
                            >
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                                <div>
                                  <span className="block text-sm text-gray-500">Payment Reference</span>
                                  <span className="font-medium text-golf-gray">{payment.reference}</span>
                                </div>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  payment.status === 'Approved'
                                    ? 'bg-green-100 text-green-800'
                                    : payment.status === 'Rejected'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {payment.status}
                                </span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                                <div>
                                  <span className="block text-sm text-gray-500">Amount</span>
                                  <span className="font-medium text-golf-gray">{payment.amount}</span>
                                </div>
                                <div>
                                  <span className="block text-sm text-gray-500">Date</span>
                                  <span className="font-medium text-golf-gray">{payment.date}</span>
                                </div>
                                <div>
                                  <span className="block text-sm text-gray-500">Proof of Payment</span>
                                  {player.payment_proof_url ? (
                                    <button
                                      type="button"
                                      onClick={handleViewProof}
                                      disabled={isLoadingProof}
                                      className="text-golf-orange hover:text-golf-green disabled:opacity-50"
                                    >
                                      {isLoadingProof ? 'Loading...' : 'View Document'}
                                    </button>
                                  ) : (
                                    <span className="text-gray-500">No proof uploaded</span>
                                  )}
                                </div>
                              </div>

                              {payment.status === 'Pending' && (
                                <div className="flex flex-col sm:flex-row items-center justify-end gap-2">
                                  <button
                                    onClick={() => handleRejectPayment(payment.id)}
                                    disabled={isProcessingPayment}
                                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {isProcessingPayment ? 'Processing...' : 'Reject'}
                                  </button>
                                  <button
                                    onClick={() => handleApprovePayment(payment.id)}
                                    disabled={isProcessingPayment}
                                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-golf-orange hover:bg-golf-green rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {isProcessingPayment ? 'Processing...' : 'Approve'}
                                  </button>
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            No payment records found for this player.
                          </div>
                        )}
                      </div>
                    </Tab.Panel>
                  </Tab.Panels>
                </Tab.Group>

                <div className="px-4 py-5 sm:p-6 border-t border-gray-200">
                  <h3 className="text-lg font-medium text-golf-gray mb-4">Special Awards</h3>

                  {loadingAwards ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-golf-orange border-t-transparent"></div>
                    </div>
                  ) : playerAwards.length > 0 ? (
                    <div className="space-y-4">
                      {playerAwards.map((award) => (
                        <div key={award.id} className="bg-amber-50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <div className="bg-amber-100 p-2 rounded-full">
                              <TrophyIcon className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <h4 className="font-medium text-golf-gray">{award.award_categories.name}</h4>
                              <p className="text-sm text-gray-600 mt-1">{award.award_categories.description}</p>
                              {award.notes && (
                                <p className="text-sm text-gray-500 mt-2 italic">"{award.notes}"</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <StarIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p>No special awards assigned to this player</p>
                    </div>
                  )}
                </div>
              </Dialog.Panel>

            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
